import { useRef, useState } from "react";
import styles from "./CopyField.module.css";

interface CopyFieldProps {
  value: string;
  label?: string;
  /** Visually de-emphasise wrapping for long opaque IDs. */
  multiline?: boolean;
}

/** Read-only monospace value with a copy-to-clipboard affordance. Used for the
 *  Org_ID, channel UUIDs, and other opaque identifiers. */
export function CopyField({ value, label, multiline = false }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const valueRef = useRef<HTMLElement>(null);

  function selectValue() {
    const el = valueRef.current;
    if (!el) return;
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setFailed(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API unavailable (insecure context / denied permission). For a
      // one-time secret a silent failure means the user loses it — fall back to
      // selecting the value and telling them to copy it manually.
      setCopied(false);
      setFailed(true);
      selectValue();
    }
  }

  return (
    <div className={styles.wrap}>
      {label && <span className={styles.label}>{label}</span>}
      <div className={styles.row}>
        <code
          ref={valueRef}
          className={[styles.value, multiline ? styles.multiline : ""].filter(Boolean).join(" ")}
        >
          {value}
        </code>
        <button type="button" className={styles.btn} onClick={copy} aria-label="Copy to clipboard">
          {copied ? (
            <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
              <path d="M3 8.5l3 3 7-7.5" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
              <rect x="5" y="5" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3.5 10.5V3.5A1.5 1.5 0 015 2h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      {failed && (
        <span className={styles.hint} role="alert">
          Couldn't reach the clipboard — the value is selected; press Ctrl/⌘+C to copy it.
        </span>
      )}
    </div>
  );
}
